import {Component, OnInit} from '@angular/core';
import {ClientService} from '../../shared/services/client.service';
import {ClientDtoModel} from '../../shared/models/client.dto.model';
import {LoaderService} from '../../shared/services/loader.service';
import {ClientGeneralInformationDtoModel} from '../../shared/models/client.general.information.dto.model';
import {UntypedFormBuilder, Validators} from '@angular/forms';
import {ErrorStateMatcher} from '@angular/material/core';
import {LocalizationService} from '../../shared/services/localization.service';
import {MessageService} from 'primeng/api';
import {AgeValidator} from '../../shared/validators/age.validator';
import {Subject} from 'rxjs';
import {debounceTime} from 'rxjs/operators';

@Component({
    selector: 'njoy-gestion-client',
    templateUrl: './gestion-client.component.html',
    styleUrls: ['./gestion-client.component.scss']
})
export class GestionClientComponent implements OnInit {

    public users: ClientGeneralInformationDtoModel[] = [];

    public addNewClientDialog = false;

    public usedEmail = false;
    public usedPhoneNumber = false;
    public errorMessage = '';

    public newClient: ClientDtoModel;
    public clientForm;
    public matcher = new ErrorStateMatcher();
    public modalTitle = '';
    private selectedUuid = '';
    private VALID_PHONE_NUMBER_REGEX = '^(?:(?:\\+|00)33|0)\\s*[6|7](?:[\\s.-]*\\d{2}){4}$';
    public isMale = false;
    public isFemale = false;
    public isOther = true;
    public nameToSearch = '';

    public filterThrottle = new Subject<string>();


    constructor(private clientService: ClientService,
                private loaderService: LoaderService,
                private formBuilder: UntypedFormBuilder,
                private localizationService: LocalizationService,
                private messageService: MessageService) {
    }

    ngOnInit(): void {
        this.getAllClients();
        this.filterThrottle.pipe(debounceTime(500)).subscribe(() => {
            this.getAllClients();
        });
    }

    public getAllClients(): void {
        this.loaderService.startLoading();
        this.clientService.getAllClients(this.nameToSearch).subscribe((data: ClientGeneralInformationDtoModel[]) => {
                this.loaderService.stopLoading();
                this.users = data;
            }, ((error: any) => {
                this.loaderService.stopLoading();
                console.log(error);
            })
        );
    }

    public openNew(): void {
        this.modalTitle = this.localizationService.translate('CLIENT.ADD_NEW_MODAL_TITLE');
        this.newClient = new ClientDtoModel();
        this.initEmptyForm();
        this.addNewClientDialog = true;
        this.setGender('other');
    }

    private initEmptyForm() {
        this.clientForm = this.formBuilder.group({
            firstName: ['', [
                Validators.required
            ]],
            lastName: ['', [
                Validators.required
            ]],
            email: ['', [
                Validators.required,
                Validators.email
            ]],
            phoneNumber: ['', [
                Validators.required,
                Validators.pattern(this.VALID_PHONE_NUMBER_REGEX)
            ]],
            birthDate: ['', [
                Validators.required,
                AgeValidator()
            ]]
            /*zipCode: ['', [
                Validators.required
            ]],*/
        });
    }

    get getControl() {
        return this.clientForm.controls;
    }

    public addOrUpdateClient() {
        if (this.modalTitle === this.localizationService.translate('CLIENT.ADD_NEW_MODAL_TITLE')) {
            // add client case
            this.addNewClient();
        } else {
            this.updateClient();
        }
    }

    private addNewClient(): void {
        this.usedPhoneNumber = false;
        this.usedEmail = false;
        this.displayRequiredMessage()
        if (this.clientForm.status === 'VALID') {
            this.loaderService.startLoading();
            this.patchValues();
            this.clientService.addNewClient(this.newClient).subscribe(() => {
                    this.addNewClientDialog = false;
                    this.getAllClients();
                    this.displaySuccessMessage('CLIENT.SUCCESS_SUMMARY', 'CLIENT.ADD_SUCCESS_DETAIL');
                }, ((error: any) => {
                    this.loaderService.stopLoading();
                    console.log(error);
                    this.errorMessage = error.error.message
                    if (error.error.code === 4005) {
                        this.usedPhoneNumber = true;
                    }
                    if (error.error.code === 4004) {
                        this.usedEmail = true;
                    }
                })
            );
        }
    }

    private displayRequiredMessage(): void {
        this.clientForm.touched = true;
        const keys = Object.keys(this.clientForm.controls);
        keys.forEach((element: any) => {
            this.clientForm.controls[element].touched = true;
        });
    }

    private updateClient(): void {
        this.usedPhoneNumber = false;
        this.usedEmail = false;
        if (this.clientForm.status === 'VALID') {
            this.loaderService.startLoading();
            this.patchValues(true);
            this.clientService.updateClient(this.newClient).subscribe(
                () => {
                    this.loaderService.stopLoading();
                    this.addNewClientDialog = false;
                    this.getAllClients();
                    this.displaySuccessMessage('CLIENT.SUCCESS_SUMMARY', 'CLIENT.UPDATE_SUCCESS_DETAIL');
                }, (err: any) => {
                    this.loaderService.stopLoading();
                    console.log('error while updating client..');
                    console.log(err);
                    this.errorMessage = err.error.message
                    if (err.error.code === 4005) {
                        this.usedPhoneNumber = true;
                    }
                    if (err.error.code === 4004) {
                        this.usedEmail = true;
                    }

                });
        }
    }

    private patchValues(initialzeClient ?: boolean): void {
        if (initialzeClient) {
            this.newClient = new ClientDtoModel();
            this.newClient.uuid = this.selectedUuid;
        }
        this.newClient.firstName = this.clientForm.value.firstName;
        this.newClient.lastName = this.clientForm.value.lastName;
        this.newClient.email = this.clientForm.value.email;
        this.newClient.phoneNumber = this.clientForm.value.phoneNumber;
        this.newClient.birthDate = this.clientForm.value.birthDate;
        if (initialzeClient) {
            this.newClient.birthDate = new Date(this.newClient.birthDate);
        }
        this.newClient.birthDate.setHours(12);
        this.newClient.sexe = this.isMale ? 'MALE' : (this.isFemale ? 'FEMALE' : 'OTHER');
    }

    public openUpdateModal(user: any) {
        this.modalTitle = this.localizationService.translate('CLIENT.UPDATE_CLIENT_MODAL_TITLE');
        this.usedPhoneNumber = false;
        this.usedEmail = false;
        this.setValues(user);
        this.addNewClientDialog = true;
        this.selectedUuid = user.uuid;
        this.setGender(user.sexe.toLowerCase());
    }

    private setValues(user: any): void {
        this.clientForm = this.formBuilder.group({
            firstName: [user.firstName, [
                Validators.required
            ]],
            lastName: [user.lastName, [
                Validators.required
            ]],
            email: [user.email, [
                Validators.required,
                Validators.email
            ]],
            phoneNumber: [user.phoneNumber, [
                Validators.required, Validators.pattern(this.VALID_PHONE_NUMBER_REGEX)
            ]],
            birthDate: [user.birthDate, [
                Validators.required
            ]],

        });
    }

    private displaySuccessMessage(summary: string, detail: string): void {
        this.messageService.add({
            severity: 'success',
            summary: this.localizationService.translate(summary),
            detail: this.localizationService.translate(detail)
        });
    }

    public setGender(gender: string): void {
        if (gender === 'male') {
            this.isMale = true;
            this.isFemale = false;
            this.isOther = false;
        } else if (gender === 'female') {
            this.isMale = false;
            this.isFemale = true;
            this.isOther = false;
        } else {
            this.isMale = false;
            this.isFemale = false;
            this.isOther = true;
        }
    }
}


